import { useEffect } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { useVoiceDictation } from "../../hooks/useVoiceDictation";

interface VoiceInputButtonProps {
    /** Current field value — what is dictated is appended to it rather than replacing it. */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Lets the host surface dictation errors in its own error slot. */
    onError?: (message: string) => void;
    /** Fires once per successful dictation, for hosts that tag where their text came from. */
    onDictated?: () => void;
    className?: string;
}

/**
 * Mic toggle for a text field, sized to sit inside a bar's input row: click to record,
 * click again to stop and transcribe. Renders nothing where the browser cannot record.
 */
export default function VoiceInputButton({
    value,
    onChange,
    disabled = false,
    onError,
    onDictated,
    className = "",
}: VoiceInputButtonProps) {
    const { supported, status, error, toggle, stop } = useVoiceDictation({
        onTranscript: text => {
            onChange(text);
            onDictated?.();
        },
    });

    useEffect(() => {
        if (error) onError?.(error);
    }, [error, onError]);

    // A field that goes disabled mid-sentence (the bar is submitting) should not leave the
    // mic open behind it.
    useEffect(() => {
        if (disabled && status === "recording") stop();
    }, [disabled, status, stop]);

    if (!supported) return null;

    const label =
        status === "recording" ? "Arrêter et transcrire" : status === "transcribing" ? "Transcription…" : "Dicter au micro";

    return (
        <button
            type="button"
            onClick={() => toggle(value)}
            disabled={disabled || status === "transcribing"}
            aria-pressed={status === "recording"}
            aria-label={label}
            title={label}
            className={`shrink-0 h-8 w-8 grid place-items-center rounded-lg border transition-colors duration-150 disabled:cursor-not-allowed ${
                status === "recording"
                    ? "border-danger bg-danger-subtle text-danger"
                    : "border-border text-ink-faint hover:text-ink hover:bg-surface-sunken disabled:opacity-40"
            } ${className}`}
        >
            {status === "recording" ? (
                <Square className="w-3 h-3 fill-current motion-safe:animate-pulse" />
            ) : status === "transcribing" ? (
                <Loader2 className="w-4 h-4 motion-safe:animate-spin" />
            ) : (
                <Mic className="w-4 h-4" />
            )}
        </button>
    );
}
